// Load matching tab (ST-00218 P2): the headline strip — current vs new matching over the routes
// shown as cards (API.md §3.1), and the bridge in one plain line:
// received − adjusted dispatched = weighing difference + receipts with no dispatch − missing loads
// − loads that need review (engine 1.1.0: a truck dispatched again before a receipt).
import type { ReactNode } from 'react';
import type { Bridge, Headline } from '../../../../types/loadMatching';
import { fmtN, fmtPct, fmtPts, fmtSignedN, fmtSignedT, fmtT, isNa } from './format';

interface Tile {
  key: string;
  label: string;
  title: string;
  now: ReactNode;
  next: ReactNode;
  sub?: ReactNode;
  delta?: ReactNode;
}

/**
 * The bridge as one sentence. Engine 1.1.0 splits the loss line into missing loads and loads that
 * need review (a ledger answer from 1.0.0 has no `needsReviewT`: the sentence is as before).
 */
export function bridgeSentence(b: Bridge): string {
  const review =
    typeof b.needsReviewT === 'number'
      ? `, loads that need review ${fmtSignedT(-b.needsReviewT)}`
      : '';
  return (
    `Received − adjusted dispatched = ${fmtSignedT(b.receivedMinusAdjustedT)}: ` +
    `weighing difference on matched loads ${fmtSignedT(b.weighingT)}, ` +
    `receipts with no matching dispatch ${fmtSignedT(b.unmatchedReceiptsT)}, ` +
    `missing loads ${fmtSignedT(-b.missingT)}${review}.`
  );
}

export default function HeadlineStrip({ headline }: { headline: Headline }) {
  const o = headline.old;
  const n = headline.new;
  const d = headline.delta;
  const na = isNa(n);
  const naText = 'n/a';

  const tiles: Tile[] = [
    {
      key: 'adjusted',
      label: 'Adjusted dispatched',
      title:
        'Dispatched + Brought Forward − Carried Forward. The new matching also leaves out loads received on another route and loads with no receipt data.',
      now: fmtT(o.adjustedT),
      next: na ? naText : fmtT(n.adjustedT),
      delta: na ? null : fmtSignedT(d.adjustedT),
    },
    {
      key: 'received',
      label: 'Received',
      title: 'Weight received on these routes in the period. The same in both.',
      now: fmtT(o.receivedT),
      next: fmtT(n.receivedT),
      sub: `${fmtN(n.receivedN)} loads`,
      delta: 'same',
    },
    {
      key: 'bf',
      label: 'Brought forward',
      title: 'Loads dispatched before the period and received in it.',
      now: `${fmtN(o.bfN)} loads`,
      next: na ? naText : `${fmtN(n.bfN)} loads`,
      sub: na ? undefined : fmtT(n.bfT),
      delta: na ? null : fmtSignedN(d.bfN),
    },
    {
      key: 'cf',
      label: 'Carried forward',
      title: 'Loads dispatched in the period that arrive after it, or are still on the road.',
      now: `${fmtN(o.cfN)} loads`,
      next: na ? naText : `${fmtN(n.cfN)} loads`,
      sub: na ? undefined : fmtT(n.cfT),
      delta: na ? null : fmtSignedN(d.cfN),
    },
    {
      key: 'missing',
      label: 'Missing loads',
      title:
        'Dispatched, never received, and the receiving site has data for that time. They stay in dispatched, so the variance shows the loss.',
      now: 'not shown',
      next: na ? naText : `${fmtN(n.missingN)} loads`,
      sub: na ? undefined : fmtT(n.missingT),
    },
    ...(typeof n.needsReviewN === 'number'
      ? [
          {
            key: 'needs-review',
            label: 'Needs review',
            title:
              'Dispatched, not received, and the same truck was dispatched again before a receipt — a probable duplicate weighing or a load to check. They stay in dispatched like missing loads.',
            now: 'not shown',
            next: na ? naText : `${fmtN(n.needsReviewN)} loads`,
            sub: na ? undefined : fmtT(n.needsReviewT),
          },
        ]
      : []),
    {
      key: 'orphans',
      label: 'Receipts with no dispatch',
      title: 'Received in the period, but no dispatch ticket matches them.',
      now: 'not shown',
      next: `${fmtN(n.orphanN)} loads`,
      sub: fmtT(n.orphanT),
    },
    {
      key: 'variance',
      label: 'Variance',
      title: '(Received − adjusted dispatched) ÷ adjusted dispatched.',
      now: fmtPct(o.variancePct),
      next: fmtPct(n.variancePct),
      delta: fmtPts(d.variancePctPoints),
    },
  ];

  return (
    <section aria-label="Headline: current vs new matching" className="mb-5">
      <div
        className={`grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 ${
          tiles.length === 8 ? '2xl:grid-cols-8' : '2xl:grid-cols-7'
        }`}
      >
        {tiles.map((t) => (
          <div
            key={t.key}
            data-testid={`headline-${t.key}`}
            title={t.title}
            className={`rounded-lg border px-3 py-2.5 bg-surface ${
              t.key === 'variance'
                ? `border-primary-300 ${tiles.length === 8 ? '' : 'col-span-2 2xl:col-span-1'}`
                : 'border-slate-200'
            }`}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              {t.label}
            </div>
            <div className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
              {t.next}
            </div>
            {t.sub && <div className="text-[11px] text-slate-500">{t.sub}</div>}
            <div className="mt-1.5 pt-1.5 border-t border-slate-100 flex flex-wrap items-baseline justify-between gap-x-2 text-[11px]">
              <span className="text-slate-500">
                Now <span className="font-medium text-slate-700">{t.now}</span>
              </span>
              {t.delta && (
                <span className="font-semibold text-slate-700 tabular-nums">{t.delta}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {!na ? (
        <p data-testid="headline-bridge" className="mt-2 text-xs text-slate-600 leading-relaxed">
          {bridgeSentence(n.bridge)}
        </p>
      ) : (
        <p className="mt-2 text-xs text-slate-600">
          The selected routes have no usable receipt data in this period, so the new figures are
          n/a.
        </p>
      )}
    </section>
  );
}
